import { t } from '@lingui/macro';

import { Boarding, Daycare, DogTraining, DropIn, Sitting, Walking } from '@rover/icons';
import type { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { SeoSlugEnum } from '@rover/shared/js/constants/seo';
import { SERVICE_TYPE_CHOICES } from '@rover/shared/js/constants/service';

import { PetType } from './Pet';
import { ServiceTypeSlug } from './ServiceType';

export enum ServiceLandingEnum {
  CAT_BOARDING = 'cat-boarding',
  CAT_SITTING = 'cat-sitting',
  DOG_BOARDING = 'dog-boarding',
  DOG_KENNEL_ALTERNATIVE = 'dog-kennel-alternative',
  DOG_TRAINING = 'dog-training',
  DOG_WALKING = 'dog-walking',
  DOGGY_DAY_CARE = 'doggy-day-care',
  HOUSE_SITTING = 'house-sitting',
  LONG_TERM_DOG_BOARDING = 'long-term-dog-boarding',
  OVERNIGHT_DOG_BOARDING = 'overnight-dog-boarding',
  PET_SITTING = 'pet-sitting',
  PUPPY_BOARDING = 'puppy-boarding',
  PUPPY_TRAINING = 'puppy-training',
}

// The purpose of these service type maps can be found in https://roverdotcom.atlassian.net/l/cp/zRRsf7r4.
export const SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM: Record<ServiceLandingEnum, ServiceTypeSlug> = {
  [ServiceLandingEnum.CAT_BOARDING]: SERVICE_TYPE_CHOICES.boarding,
  [ServiceLandingEnum.CAT_SITTING]: SERVICE_TYPE_CHOICES.dropIn,
  [ServiceLandingEnum.DOG_BOARDING]: SERVICE_TYPE_CHOICES.boarding,
  [ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE]: SERVICE_TYPE_CHOICES.boarding,
  [ServiceLandingEnum.DOG_TRAINING]: SERVICE_TYPE_CHOICES.training,
  [ServiceLandingEnum.DOG_WALKING]: SERVICE_TYPE_CHOICES.walking,
  [ServiceLandingEnum.DOGGY_DAY_CARE]: SERVICE_TYPE_CHOICES.dayCare,
  [ServiceLandingEnum.HOUSE_SITTING]: SERVICE_TYPE_CHOICES.traveling,
  [ServiceLandingEnum.LONG_TERM_DOG_BOARDING]: SERVICE_TYPE_CHOICES.boarding,
  [ServiceLandingEnum.OVERNIGHT_DOG_BOARDING]: SERVICE_TYPE_CHOICES.boarding,
  [ServiceLandingEnum.PET_SITTING]: SERVICE_TYPE_CHOICES.dropIn,
  [ServiceLandingEnum.PUPPY_BOARDING]: SERVICE_TYPE_CHOICES.boarding,
  [ServiceLandingEnum.PUPPY_TRAINING]: SERVICE_TYPE_CHOICES.training,
};

export const SEO_SLUG_FOR_SERVICE_LANDING_ENUM: Record<
  ServiceLandingEnum,
  Exclude<SeoSlugEnum, SeoSlugEnum.DOG_SITTING | SeoSlugEnum.PET_BOARDING> | null
> = {
  [ServiceLandingEnum.CAT_BOARDING]: SeoSlugEnum.CAT_SITTING,
  [ServiceLandingEnum.CAT_SITTING]: SeoSlugEnum.CAT_SITTING,
  [ServiceLandingEnum.DOG_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.DOG_TRAINING]: SeoSlugEnum.DOG_TRAINING,
  [ServiceLandingEnum.DOG_WALKING]: SeoSlugEnum.DOG_WALKING,
  [ServiceLandingEnum.DOGGY_DAY_CARE]: SeoSlugEnum.DOGGY_DAY_CARE,
  [ServiceLandingEnum.HOUSE_SITTING]: SeoSlugEnum.HOUSE_SITTING,
  [ServiceLandingEnum.LONG_TERM_DOG_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.OVERNIGHT_DOG_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.PET_SITTING]: SeoSlugEnum.PET_SITTING,
  [ServiceLandingEnum.PUPPY_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.PUPPY_TRAINING]: SeoSlugEnum.DOG_TRAINING,
};

export const SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM: Record<
  ServiceLandingEnum,
  ServiceTypeSlugEnum
> = {
  [ServiceLandingEnum.CAT_BOARDING]: SeoSlugEnum.CAT_SITTING,
  [ServiceLandingEnum.CAT_SITTING]: SeoSlugEnum.CAT_SITTING,
  [ServiceLandingEnum.DOG_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.DOG_TRAINING]: SeoSlugEnum.DOG_TRAINING,
  [ServiceLandingEnum.DOG_WALKING]: SeoSlugEnum.DOG_WALKING,
  [ServiceLandingEnum.DOGGY_DAY_CARE]: SeoSlugEnum.DOGGY_DAY_CARE,
  [ServiceLandingEnum.HOUSE_SITTING]: SeoSlugEnum.HOUSE_SITTING,
  [ServiceLandingEnum.LONG_TERM_DOG_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.OVERNIGHT_DOG_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.PET_SITTING]: SeoSlugEnum.PET_SITTING,
  [ServiceLandingEnum.PUPPY_BOARDING]: SeoSlugEnum.DOG_BOARDING,
  [ServiceLandingEnum.PUPPY_TRAINING]: SeoSlugEnum.DOG_TRAINING,
};

export const SERVICE_LANDING_FOR_SEO_SLUG_ENUM: Record<
  Exclude<SeoSlugEnum, SeoSlugEnum.DOG_SITTING | SeoSlugEnum.PET_BOARDING>,
  ServiceLandingEnum
> = {
  [SeoSlugEnum.DOG_BOARDING]: ServiceLandingEnum.DOG_BOARDING,
  [SeoSlugEnum.DOG_WALKING]: ServiceLandingEnum.DOG_WALKING,
  [SeoSlugEnum.DOGGY_DAY_CARE]: ServiceLandingEnum.DOGGY_DAY_CARE,
  [SeoSlugEnum.HOUSE_SITTING]: ServiceLandingEnum.HOUSE_SITTING,
  [SeoSlugEnum.CAT_SITTING]: ServiceLandingEnum.CAT_SITTING,
  [SeoSlugEnum.PET_SITTING]: ServiceLandingEnum.PET_SITTING,
  [SeoSlugEnum.DOG_TRAINING]: ServiceLandingEnum.DOG_TRAINING,
};

export const DISPLAY_NAME_FOR_SERVICE_LANDING_ENUM = (): Record<ServiceLandingEnum, string> => ({
  [ServiceLandingEnum.DOG_BOARDING]: t`Dog Boarding`,
  [ServiceLandingEnum.PUPPY_BOARDING]: t`Puppy Boarding`,
  [ServiceLandingEnum.DOG_WALKING]: t`Dog Walking`,
  [ServiceLandingEnum.DOGGY_DAY_CARE]: t`Doggy Day Care`,
  [ServiceLandingEnum.LONG_TERM_DOG_BOARDING]: t`Long Term Dog Boarding`,
  [ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE]: t`Dog Kennel Alternative`,
  [ServiceLandingEnum.CAT_SITTING]: t`Cat Sitting`,
  [ServiceLandingEnum.CAT_BOARDING]: t`Cat Boarding`,
  [ServiceLandingEnum.HOUSE_SITTING]: t`House Sitting`,
  [ServiceLandingEnum.PET_SITTING]: t`Pet Sitting`,
  [ServiceLandingEnum.OVERNIGHT_DOG_BOARDING]: t`Overnight Dog Boarding`,
  [ServiceLandingEnum.DOG_TRAINING]: t`Dog Training`,
  [ServiceLandingEnum.PUPPY_TRAINING]: t`Puppy Training`,
});

/*
 * This is an incomplete set - this is all that's currently in use.
 * Please add icons as needed
 */
export const ICON_FOR_SERVICE_LANDING_ENUM = {
  [ServiceLandingEnum.DOG_BOARDING]: Boarding,
  [ServiceLandingEnum.HOUSE_SITTING]: Sitting,
  [ServiceLandingEnum.PET_SITTING]: DropIn,
  [ServiceLandingEnum.DOGGY_DAY_CARE]: Daycare,
  [ServiceLandingEnum.DOG_WALKING]: Walking,
  // note: the training icon is not the same size as the others
  [ServiceLandingEnum.DOG_TRAINING]: DogTraining,
};

export const isServiceLanding = (value: unknown): value is ServiceLandingEnum =>
  Object.values(ServiceLandingEnum).includes(value as ServiceLandingEnum);

export const PET_TYPE_FOR_SERVICE_LANDING_ENUM = {
  [ServiceLandingEnum.CAT_BOARDING]: [PetType.CAT],
  [ServiceLandingEnum.CAT_SITTING]: [PetType.CAT],
  [ServiceLandingEnum.DOG_BOARDING]: [PetType.DOG],
  [ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE]: [PetType.DOG],
  [ServiceLandingEnum.DOG_TRAINING]: [PetType.DOG],
  [ServiceLandingEnum.DOG_WALKING]: [PetType.DOG],
  [ServiceLandingEnum.DOGGY_DAY_CARE]: [PetType.DOG],
  [ServiceLandingEnum.HOUSE_SITTING]: [PetType.DOG],
  [ServiceLandingEnum.LONG_TERM_DOG_BOARDING]: [PetType.DOG],
  [ServiceLandingEnum.OVERNIGHT_DOG_BOARDING]: [PetType.DOG],
  [ServiceLandingEnum.PET_SITTING]: [PetType.DOG],
  [ServiceLandingEnum.PUPPY_BOARDING]: [PetType.DOG],
  [ServiceLandingEnum.PUPPY_TRAINING]: [PetType.DOG],
};
